import { bayer, type PaletteKey, type PixelCanvas, sprite } from '../../lib';

// Crate at 4× density (ADR-0111): 64×96 art px = 16×24 world px, 3/4 view. Body x 2-61 inside a 2-px warm sel-out:
// the open top rows 14-59 (back rim, the apple heap, the front rim), the front face rows 60-93, ground line rows 94-95.
const X0 = 2;
const X1 = 61;
const TOP0 = 14;
const RIM = 56;
const FR0 = 60;
const FR1 = 93;
/** The heap sits between the side rims. */
const IN0 = 6;
const IN1 = 57;

/** Light from the top-left, a little in front. */
const L = (() => {
  const [x, y, z] = [-0.5, -0.7, 0.55];
  const n = Math.hypot(x, y, z);
  return [x / n, y / n, z / n] as const;
})();

/**
 * A round apple centred on (cx, cy): a glossy lit volume (a deep rim on its shadow side so it parts from the fruit
 * behind, a soft highlight and a specular glint top-left), a dimple and a stem on top, a leaf on some.
 */
function apple(c: PixelCanvas, cx: number, cy: number, green: boolean, leaf: -1 | 0 | 1): void {
  const [deep, shade, body, lit, spec] = (green ? ['D', 'G', 'g', 'l', 'y'] : ['N', 'R', 'r', 'i', 'w']) as readonly [
    PaletteKey,
    PaletteKey,
    PaletteKey,
    PaletteKey,
    PaletteKey,
  ];
  const rx = 7.6;
  const ry = 7.1;
  const inside = (x: number, y: number) => ((x + 0.5 - cx) / rx) ** 2 + ((y + 0.5 - cy) / ry) ** 2 <= 1;
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.max(IN0, Math.floor(cx - rx)); x <= Math.min(IN1, Math.ceil(cx + rx)); x++) {
      if (!inside(x, y)) continue;
      const nx = (x + 0.5 - cx) / rx;
      const ny = (y + 0.5 - cy) / ry;
      const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
      const dot = nx * L[0] + ny * L[1] + nz * L[2];
      const edge = !inside(x + 1, y) || !inside(x, y + 1) || !inside(x - 1, y) || !inside(x, y - 1);
      let tone = dot < 0.2 ? shade : dot < 0.86 ? body : lit;
      if (edge && nx + ny > -0.2) tone = deep;
      // Bounce light off the fruit below: the bottom-left rim a step up from the shade.
      if (!edge && tone === shade && ny > 0.62 && nx < 0.1) tone = body;
      c.set(x, y, tone);
    }
  }
  // The glint: two px of specular light in the highlight.
  const gx = Math.round(cx - rx * 0.42);
  const gy = Math.round(cy - ry * 0.5);
  c.set(gx, gy, spec)
    .set(gx + 1, gy, spec)
    .set(gx, gy + 1, lit);
  // Dimple and stem: a short dark stalk leaning right out of a shaded notch.
  const top = Math.round(cy - ry);
  const sx = Math.round(cx);
  c.set(sx - 1, top + 1, shade)
    .set(sx + 1, top + 1, shade)
    .set(sx, top + 1, deep);
  c.set(sx, top, 'N')
    .set(sx, top - 1, 'N')
    .set(sx + 1, top - 2, 'n');
  if (leaf !== 0) {
    // A small leaf off the stalk: lit top, dark midrib under it.
    const d = leaf;
    c.set(sx + d, top - 1, 'l')
      .set(sx + 2 * d, top - 2, 'l')
      .set(sx + 3 * d, top - 2, 'l')
      .set(sx + 4 * d, top - 2, 'g');
    c.set(sx + 2 * d, top - 1, 'g')
      .set(sx + 3 * d, top - 1, 'G')
      .set(sx + 4 * d, top - 3, 'G');
  }
}

/** The heap, back row first; the twinkles of `crate-view.ts` (APPLE_SPOTS) land on these. */
const APPLES: readonly [x: number, y: number, green: boolean, leaf: -1 | 0 | 1][] = [
  [14, 26, false, 1],
  [30, 26, true, 0],
  [46, 26, false, -1],
  [22, 38, false, 0],
  [38, 38, false, 1],
  [54, 38, true, 0],
  [14, 50, true, 0],
  [30, 50, false, -1],
  [46, 50, false, 0],
];

/** 1-px wood grain on a board: [x0, x1, y] runs; a run steps a row at its middle when `wave` is set. */
function grain(c: PixelCanvas, runs: readonly (readonly [number, number, number, number?])[], col: PaletteKey): void {
  for (const [x0, x1, y, wave] of runs) {
    const mid = Math.round((x0 + x1) / 2);
    for (let x = x0; x <= x1; x++) c.set(x, y + (wave && x > mid ? wave : 0), col);
  }
}

/** A round iron nail head, lit top-left, its shadow on the wood bottom-right. */
function nail(c: PixelCanvas, x: number, y: number): void {
  c.set(x, y, '4')
    .set(x + 1, y, '3')
    .set(x, y + 1, '3')
    .set(x + 1, y + 1, '2');
  c.set(x + 2, y + 1, 'n')
    .set(x + 1, y + 2, 'n')
    .set(x + 2, y + 2, 'n');
}

/**
 * Warm sel-out, 2 px: each outline pixel is `map[colour it wraps]` (else `N`), the second ring copies the first;
 * the ground line under the crate (rows 94-95) is `N`.
 */
function crateOutline(c: PixelCanvas, map: Partial<Record<PaletteKey, PaletteKey>>): void {
  for (const pass of [0, 1]) {
    const add: [number, number, PaletteKey][] = [];
    for (let y = 0; y < c.h; y++) {
      for (let x = 0; x < c.w; x++) {
        if (c.get(x, y) !== null) continue;
        const up = c.get(x, y - 1);
        const src = up ?? c.get(x - 1, y) ?? c.get(x + 1, y) ?? c.get(x, y + 1);
        if (src === null) continue;
        add.push([x, y, up !== null && y > FR1 ? 'N' : pass ? src : (map[src] ?? 'N')]);
      }
    }
    for (const [x, y, k] of add) c.set(x, y, k);
  }
}

function drawCrate(c: PixelCanvas, missing: readonly number[] = []): void {
  // The dark inside, the back rim (the back board's top, lit), the back board's inner face in shadow.
  c.rect(X0, TOP0, X1 - X0 + 1, RIM - TOP0, 'N');
  c.hline(X0, X1, TOP0, 'e')
    .rect(X0, TOP0 + 1, X1 - X0 + 1, 2, 't')
    .hline(X0, X1, TOP0 + 3, 'm');
  c.rect(X0, TOP0 + 4, X1 - X0 + 1, 3, 'n');
  grain(c, [[36, 48, TOP0 + 2]], 'm');
  // Apples, back row first.
  APPLES.forEach(([x, y, green, leaf], i) => {
    if (!missing.includes(i)) apple(c, x, y, green, leaf);
  });
  // The side rims (the side boards' tops) over any apple that overhangs them: lit left, shaded right.
  c.rect(X0, TOP0, 4, RIM - TOP0, 't')
    .vline(X0, TOP0, RIM - 1, 'e')
    .vline(X0 + 3, TOP0 + 4, RIM - 1, 'm');
  c.rect(X1 - 3, TOP0, 4, RIM - TOP0, 'm')
    .vline(X1 - 3, TOP0, RIM - 1, 't')
    .vline(X1, TOP0 + 1, RIM - 1, 'n');
  c.hline(X0, X1, TOP0, 'e').set(X1, TOP0, 't');
  grain(
    c,
    [
      [X0 + 1, X0 + 1, TOP0 + 8],
      [X0 + 2, X0 + 2, TOP0 + 22],
    ],
    'm',
  );
  // Under the front rim the heap falls into shadow.
  c.rect(X0 + 4, RIM - 2, X1 - X0 - 7, 2, 'N');
  // The front rim: the front board's top catches the light, its edge turns down into the face.
  c.hline(X0, X1, RIM, 't')
    .rect(X0, RIM + 1, X1 - X0 + 1, 2, 'e')
    .hline(X0, X1, RIM + 3, 't');
  c.set(X1, RIM + 1, 't').set(X1, RIM + 2, 't');
  grain(
    c,
    [
      [16, 30, RIM + 2],
      [42, 52, RIM + 1, 1],
    ],
    't',
  );
  // Front face: two slats with a dark gap, lit along their top edge, shaded along the bottom, 1-px grain.
  for (const [y0, y1] of [
    [FR0, 75],
    [78, FR1],
  ] as const) {
    c.rect(X0, y0, X1 - X0 + 1, y1 - y0 + 1, 'm');
    c.hline(X0, X1, y0, 't');
    c.hline(X0, X1, y1, 'n');
  }
  c.rect(X0, 76, X1 - X0 + 1, 2, 'N');
  grain(
    c,
    [
      [12, 31, 64, 1],
      [35, 52, 66],
      [15, 41, 70, -1],
      [45, 52, 72],
      [12, 34, 82, 1],
      [44, 52, 83],
      [26, 37, 86],
      [43, 52, 86],
      [14, 23, 89],
      [28, 46, 90, -1],
    ],
    'n',
  );
  // A knot on the lower slat, the grain parting round it.
  c.hline(39, 41, 85, 'n').set(38, 86, 'n').set(42, 86, 'n').hline(39, 41, 87, 'n').hline(39, 41, 86, 'N');
  c.set(37, 85, 'n').set(43, 85, 'n');
  grain(
    c,
    [
      [14, 24, 61],
      [36, 44, 79],
    ],
    'e',
  );
  // Corner posts over the slats: the left lit down its outer edge and casting a 1-px shadow on the slats, the right
  // lit down its inner edge, shaded outside. A nail through each post into each slat.
  c.rect(X0, FR0, 8, FR1 - FR0 + 1, 'm')
    .vline(X0, FR0, FR1, 't')
    .vline(X0 + 1, FR0, FR1, 't');
  c.vline(X0 + 7, FR0, FR1, 'n').vline(X0 + 8, FR0 + 1, FR1, 'n');
  c.rect(X1 - 7, FR0, 8, FR1 - FR0 + 1, 'm')
    .vline(X1 - 7, FR0, FR1, 't')
    .vline(X1, FR0, FR1, 'n');
  c.vline(X1 - 1, FR0 + 2, FR1, 'n');
  c.hline(X0, X0 + 7, FR0, 'e').hline(X1 - 7, X1 - 1, FR0, 't');
  c.hline(X0, X0 + 7, FR1, 'n').hline(X1 - 7, X1, FR1, 'N');
  grain(
    c,
    [
      [X0 + 4, X0 + 4, 64],
      [X0 + 4, X0 + 4, 65],
      [X0 + 5, X0 + 5, 83],
      [X0 + 5, X0 + 5, 84],
      [X1 - 4, X1 - 4, 70],
      [X1 - 4, X1 - 4, 71],
      [X1 - 3, X1 - 3, 88],
      [X1 - 3, X1 - 3, 89],
    ],
    'n',
  );
  for (const x of [X0 + 3, X1 - 4]) for (const y of [66, 84]) nail(c, x, y);
  crateOutline(c, {
    t: 'n',
    e: 'n',
    m: 'n',
    n: 'N',
    N: 'N',
    r: 'R',
    R: 'N',
    i: 'R',
    w: 'R',
    g: 'G',
    l: 'G',
    y: 'G',
    G: 'D',
    D: 'D',
  });
}

/** Hit hard (ADR-0068: it breaks): split slats, two apples knocked out of the heap, a corner chipped off. */
function drawCracked(c: PixelCanvas): void {
  drawCrate(c, [1, 5]);
  // A split down through both slats, zigzagging: a dark gap with a lit splinter edge on its left.
  const split = (pts: readonly (readonly [number, number])[]) => {
    for (let i = 1; i < pts.length; i++) {
      const [x0, y0] = pts[i - 1] as readonly [number, number];
      const [x1, y1] = pts[i] as readonly [number, number];
      c.line(x0, y0, x1, y1, 'N')
        .line(x0 + 1, y0, x1 + 1, y1, 'N')
        .line(x0 - 1, y0, x1 - 1, y1, 't');
    }
  };
  split([
    [17, FR0],
    [20, 65],
    [22, 70],
    [25, 75],
  ]);
  split([
    [25, 78],
    [27, 84],
    [26, 88],
    [29, FR1],
  ]);
  split([
    [45, FR0 + 1],
    [43, 66],
    [44, 70],
    [41, 75],
  ]);
  c.line(40, 78, 38, 81, 'N').line(39, 78, 37, 81, 't');
  // The top-right corner chipped off the front: a notch, its torn edge dark.
  for (let y = FR0; y <= FR0 + 5; y++) for (let x = X1 - 6 + (y - FR0); x <= X1 + 2; x++) c.set(x, y, null);
  for (let y = RIM + 2; y < FR0; y++) for (let x = X1 - 2 + (y - RIM - 2); x <= X1 + 2; x++) c.set(x, y, null);
  for (let y = FR0; y <= FR0 + 6; y++) c.set(X1 - 7 + (y - FR0), y, 'N');
}

const crate = sprite({
  name: 'crate',
  seed: 'crate_a',
  group: 'prop',
  size: [64, 96],
  origin: [0, 32],
  density: 4,
  flash: true,
  notes: `"Storybook" (A) direction, picked in the 2026-09 art review. Cover. Open slatted crate heaped with red
and green apples, soft warm sel-out (2 px), slat gap. 4× density (ADR-0111): 64×96 art px = 16×24 world px.
Planks with 1-px wood grain and a knot, nail heads on the corner posts, lit rim edges; the apples round and glossy (a
deep rim on the shadow side, a pink highlight and a white glint, a stalk, a leaf on some).
Breakable since ADR-0068: \`cracked\` under half its health (split slats, apples knocked out of the heap, a notch).
Rubric (4×): S5 R5 P4 L4 C4 A- F4. Weakest: the bright red heap still pulls the eye (could hint "red team"); the
leaves on the apples are only a few px.`,
  anims: { idle: { frames: [(c: PixelCanvas) => drawCrate(c)] }, cracked: { frames: [drawCracked] } },
});

/**
 * A soft contact shadow: a solid core whose edge is dithered out (ordered, 1 art px), drawn in k at ~30% alpha by the
 * renderer. `soft` is how far in from the rim the dither starts (0..1 of the radius).
 */
const softShadow = (cx: number, cy: number, rx: number, ry: number, soft: number) => (c: PixelCanvas) => {
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      const d = Math.hypot((x + 0.5 - cx) / rx, (y + 0.5 - cy) / ry);
      if (d > 1) continue;
      const k = (1 - d) / soft; // 0 at the rim, 1 where the core begins
      if (k >= 1 || bayer(x, y) < k) c.set(x, y, 'k');
    }
  }
};

const shadow = sprite({
  name: 'shadow',
  group: 'fx',
  size: [48, 20],
  origin: [24, 8],
  density: 4,
  notes: `Contact shadow under actors; drawn in k at ~30% alpha by the renderer. 4× density (ADR-0111): the 12×5 world
ellipse with a solid core and an ordered 1-art-px dither fading its rim, so it lands soft. Rubric: S- R4 P5 L- C4 F4.
Weakest: views scale it by non-integer factors (1.3×1.2 under a cannon), which can bunch the dither a little.`,
  anims: { idle: { frames: [softShadow(24, 10, 24, 10, 0.4)] } },
});

const shadowBig = sprite({
  name: 'shadow_big',
  group: 'fx',
  size: [104, 32],
  origin: [52, 16],
  density: 4,
  notes:
    'Big contact shadow (trees, bases, bosses, crates); 4× density (ADR-0111), its rim dithered out like `shadow`. ' +
    'Rubric: S- R4 P5 L- C4 F4 (same weakness as `shadow`).',
  anims: { idle: { frames: [softShadow(52, 16, 52, 16, 0.35)] } },
});

export default [crate, shadow, shadowBig];
